import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

import { authClient } from "@/lib/auth-client";
import { GoogleSignInButton } from "@/components/common/google-signin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldError,
} from "@/components/ui/field";

const schema = z.object({
  email: z.string().email("Correo inválido"),
  password: z.string().min(1, "Ingresa tu contraseña"),
});
type FormValues = z.infer<typeof schema>;

// Los dos códigos que de verdad ocurren cuando el callback de Google falla.
// El resto cae al mensaje genérico.
const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Cancelaste el ingreso con Google.",
  account_not_linked:
    "Ese correo ya tiene una cuenta sin confirmar. Revisa tu bandeja para activarla.",
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const googleError = searchParams.get("error");
  useEffect(() => {
    if (!googleError) return;
    // El `id` evita el toast duplicado del doble render de StrictMode.
    toast.error(
      GOOGLE_ERROR_MESSAGES[googleError] ||
        "No pudimos completar el ingreso con Google. Inténtalo de nuevo.",
      { id: "google-error" },
    );
    navigate("/auth/login", { replace: true });
  }, [googleError, navigate]);

  async function onSubmit(values: FormValues) {
    const { error } = await authClient.signIn.email({
      email: values.email,
      password: values.password,
    });
    if (error) {
      // El backend reenvía el enlace solo, así que basta con avisar.
      if (error.code === "EMAIL_NOT_VERIFIED") {
        toast.error(
          "Todavía no confirmas tu correo. Te reenviamos el enlace, revisa tu bandeja.",
        );
        return;
      }
      toast.error(error.message || "No pudimos iniciar sesión");
      return;
    }
    // A la raíz, no al dashboard: a dónde entra el usuario lo decide su plan
    // (ver `getLandingRoute`), y esa decisión vive en un solo sitio.
    navigate("/");
  }

  return (
    <div>
      <h1 className="text-center text-3xl font-extrabold leading-tight tracking-tight">
        Iniciar Sesión
      </h1>
      <p className="mt-0.5 flex items-baseline justify-center gap-1 text-sm font-medium">
        <span>¿No tienes una cuenta?</span>
        <Link to="/auth/register" className="text-primary hover:underline">
          Crea una
        </Link>
      </p>

      <GoogleSignInButton />

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">
              Correo <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              {...register("email")}
            />
            <FieldError errors={[errors.email]} />
          </Field>
          <Field>
            <FieldLabel htmlFor="password">
              Contraseña <span className="text-destructive">*</span>
            </FieldLabel>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="pr-10"
                {...register("password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <FieldError errors={[errors.password]} />
          </Field>
          <div className="-mt-3 flex justify-end">
            <Link
              to="/auth/forgot-password"
              className="text-sm font-medium text-primary hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Ingresando..." : "Iniciar sesión"}
          </Button>
        </FieldGroup>
      </form>
    </div>
  );
}
